
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { GalleryPhoto } from "@/components/gallery/carousel/types";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

export function useGalleryPhotos(userId: string | undefined) {
  const [recentPhotos, setRecentPhotos] = useState<GalleryPhoto[]>([]);
  const [milestonePhotos, setMilestonePhotos] = useState<GalleryPhoto[]>([]);
  const [collectionPhotos, setCollectionPhotos] = useState<Record<string, GalleryPhoto[]>>({});
  const [pregnancyPhotos, setPregnancyPhotos] = useState<Record<string, GalleryPhoto[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();

  const fetchGalleryPhotos = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    console.log("Fetching gallery photos for user:", userId);
    
    const startTime = performance.now();
    
    try {
      // Fetch recent photos - don't limit the number
      const { data: recentData, error: recentError } = await supabase
        .from('gallery_photos')
        .select('id, url, title, category, user_id, created_at')
        .eq('user_id', userId)
        .eq('category', 'recent')
        .order('created_at', { ascending: false });

      if (recentError) throw recentError;
      
      // Deduplicate by URL - keep only the first occurrence of each URL
      const uniqueRecent = recentData?.reduce((acc: GalleryPhoto[], current) => {
        const exists = acc.find(item => item.url === current.url);
        if (!exists) {
          acc.push(current as GalleryPhoto);
        }
        return acc;
      }, []);
      
      console.log(`Fetched ${recentData?.length || 0} recent photos, ${uniqueRecent.length} unique`);
      setRecentPhotos(uniqueRecent);

      // Fetch milestone photos
      const { data: milestoneData, error: milestoneError } = await supabase
        .from('gallery_photos')
        .select('id, url, title, category, user_id, created_at')
        .eq('user_id', userId)
        .eq('category', 'milestone')
        .order('created_at', { ascending: false });

      if (milestoneError) throw milestoneError;
      
      // Deduplicate milestone photos
      const uniqueMilestones = milestoneData?.reduce((acc: GalleryPhoto[], current) => {
        const exists = acc.find(item => item.url === current.url);
        if (!exists) {
          acc.push(current as GalleryPhoto);
        }
        return acc;
      }, []);
      
      console.log(`Fetched ${milestoneData?.length || 0} milestone photos, ${uniqueMilestones.length} unique`);
      setMilestonePhotos(uniqueMilestones);

      // Fetch collection photos
      const collections = ['first-year', 'family-moments', 'special-events'];
      const collectionsData: Record<string, GalleryPhoto[]> = {};
      
      for (const collection of collections) {
        const { data, error } = await supabase
          .from('gallery_photos')
          .select('id, url, title, category, user_id, created_at')
          .eq('user_id', userId)
          .eq('category', collection)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        // Deduplicate collection photos
        const uniqueCollection = data?.reduce((acc: GalleryPhoto[], current) => {
          const exists = acc.find(item => item.url === current.url);
          if (!exists) {
            acc.push(current as GalleryPhoto);
          }
          return acc;
        }, []);
        
        collectionsData[collection] = uniqueCollection;
        console.log(`Fetched ${data?.length || 0} photos for collection: ${collection}, ${uniqueCollection.length} unique`);
      }
      
      setCollectionPhotos(collectionsData);
      
      // Fetch pregnancy photos - these are categorized by week (e.g., 'pregnancy-week-12')
      const { data: pregnancyData, error: pregnancyError } = await supabase
        .from('gallery_photos')
        .select('id, url, title, category, user_id, created_at')
        .eq('user_id', userId)
        .like('category', 'pregnancy-week-%')
        .order('created_at', { ascending: false });
        
      if (pregnancyError) throw pregnancyError;
      
      console.log(`Fetched ${pregnancyData?.length || 0} pregnancy photos`);
      
      // Group pregnancy photos by week
      const pregnancyByWeek: Record<string, GalleryPhoto[]> = {};
      
      (pregnancyData || []).forEach((photo: any) => {
        const weekMatch = photo.category.match(/pregnancy-week-(\d+)/);
        if (weekMatch && weekMatch[1]) {
          const week = `Week ${weekMatch[1]}`;
          if (!pregnancyByWeek[week]) {
            pregnancyByWeek[week] = [];
          }
          
          // Only add if not already exists with the same URL
          const exists = pregnancyByWeek[week].some(item => item.url === photo.url);
          if (!exists) {
            pregnancyByWeek[week].push(photo as GalleryPhoto);
          }
        }
      });
      
      setPregnancyPhotos(pregnancyByWeek);
      
      const endTime = performance.now();
      console.log(`Gallery photos loaded in ${endTime - startTime}ms`);
    } catch (error) {
      console.error("Error fetching gallery photos:", error);
      toast.error(t('gallery.photoLoadingFailed', language) || "Failed to load gallery photos");
    } finally {
      setIsLoading(false);
    }
  }, [userId, language]);

  // Initial fetch when component mounts or userId changes
  useEffect(() => {
    if (userId) {
      console.log("Initial gallery photos fetch");
      fetchGalleryPhotos();
    }
  }, [userId, fetchGalleryPhotos]);

  // Add a subscription to gallery changes for realtime updates
  useEffect(() => {
    if (!userId) return;
    
    console.log("Setting up realtime subscription for gallery changes");
    
    // Subscribe to all changes on the gallery_photos table for this user
    const channel = supabase
      .channel('gallery_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'gallery_photos',
          filter: `user_id=eq.${userId}`
        }, 
        (payload) => {
          console.log("Gallery change detected:", payload.eventType);
          // Refresh gallery photos when changes detected
          fetchGalleryPhotos();
        }
      )
      .subscribe();
      
    // Subscribe to changes on the diary_entries table that might contain images
    const diaryChannel = supabase
      .channel('diary_images_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'diary_entries',
          filter: `user_id=eq.${userId}`
        }, 
        (payload) => {
          console.log("Diary entry change detected:", payload.eventType);
          // Fix: Check if payload.new exists and if it has images property before accessing it
          if (payload.new && typeof payload.new === 'object' && 'images' in payload.new) {
            console.log("Diary entry with images changed, refreshing gallery");
            fetchGalleryPhotos();
          }
        }
      )
      .subscribe();
      
    return () => {
      console.log("Cleaning up gallery subscriptions");
      supabase.removeChannel(channel);
      supabase.removeChannel(diaryChannel);
    };
  }, [userId, fetchGalleryPhotos]);

  return {
    recentPhotos,
    milestonePhotos,
    collectionPhotos,
    pregnancyPhotos,
    isLoading,
    refetch: fetchGalleryPhotos
  };
}
